/* ============================================================
   Fence Works — recurring care plans (docs/SERVICE_SWITCHER_PLAN.md §13
   item 15). Pure, import-free: the unit harness transpiles it alone.

   A sealed driveway wants re-sealing every two years, caulked joints and a
   cleaning every year. When a concrete job finishes, each of those job types
   on it becomes a plan on the CUSTOMER (customer.carePlans[]): what, how
   often, the scope it covered, when it's next due. The To-Do surfaces a plan
   45 days out, the customer's page books it in one tap (rebookBlocks → a
   fresh concrete estimate priced off today's rate cards), and finishing the
   rebooked job rolls the plan forward. The hub shows the customer their own
   plan (server/src/hubscope.js allowlists jobType / nextDue / everyMonths).
   ============================================================ */
export type CareType = "seal" | "caulk" | "clean";
export type CarePlan = {
  id: string; vertical: "concrete"; jobType: CareType; everyMonths: number;
  nextDue: string; lastDone: string | null;
  sourceQuoteId: string | null; sourceWoId: string | null; lastQuoteId?: string | null;
  scope: { sqft?: number; ft?: number; surfaces?: string[] };
  status: "active" | "paused"; createdAt: number; remindedFor?: string | null;
};

export const CARE_TYPES: CareType[] = ["seal", "caulk", "clean"];
export const CARE_EVERY: Record<CareType, number> = { seal: 24, caulk: 12, clean: 12 };
export const CARE_LABEL: Record<CareType, string> = { seal: "Re-seal", caulk: "Re-caulk", clean: "Cleaning" };
export const CARE_VERB: Record<CareType, string> = { seal: "sealed", caulk: "caulked", clean: "cleaned" };
const YMD = /^\d{4}-\d{2}-\d{2}$/;
const pad = (n: number) => String(n).padStart(2, "0");

export const isCareType = (x: any): x is CareType => CARE_TYPES.indexOf(x) > -1;
export function monthLen(y: number, m0: number): number { return new Date(y, m0 + 1, 0).getDate(); }
/* "YYYY-MM-DD" + n months, the day clamped to the month's length (Jan 31 + 1 → Feb 28) */
export function addMonths(ymd: string, n: number): string {
  if (typeof ymd !== "string" || !YMD.test(ymd)) return ymd;
  const [y, m, d] = ymd.split("-").map(Number);
  const t = (m - 1) + (Math.round(Number(n)) || 0);
  const y2 = y + Math.floor(t / 12), m2 = ((t % 12) + 12) % 12;
  return y2 + "-" + pad(m2 + 1) + "-" + pad(Math.min(d, monthLen(y2, m2)));
}
/* whole days from `fromYmd` to `toYmd` (negative = already past) */
export function daysBetween(fromYmd: string, toYmd: string): number {
  if (!YMD.test(String(fromYmd)) || !YMD.test(String(toYmd))) return 0;
  const a = Date.UTC(+fromYmd.slice(0, 4), +fromYmd.slice(5, 7) - 1, +fromYmd.slice(8, 10));
  const b = Date.UTC(+toYmd.slice(0, 4), +toYmd.slice(5, 7) - 1, +toYmd.slice(8, 10));
  return Math.round((b - a) / 86400000);
}
export function everyLabel(months: number): string {
  const m = Math.round(Number(months) || 0);
  if (m === 12) return "every year";
  if (m > 0 && m % 12 === 0) return "every " + (m / 12) + " years";
  return m > 0 ? "every " + m + " months" : "";
}

/* the care-type blocks a finished job carried (wo.cx.blocks, the cost-free
   scope every concrete job carries; app.tsx addProjectFromQuote) */
function careBlocksOf(wo: any): any[] {
  const blocks = wo && wo.cx && Array.isArray(wo.cx.blocks) ? wo.cx.blocks : [];
  return blocks.filter((b: any) => b && isCareType(b.jobType));
}
function scopeOf(blocks: any[]): CarePlan["scope"] {
  const out: CarePlan["scope"] = {};
  const sqft = blocks.reduce((s, b) => s + (Number(b.sqft) || 0), 0);
  const ft = blocks.reduce((s, b) => s + (Number(b.ft) || 0), 0);
  if (sqft > 0) out.sqft = Math.round(sqft);
  if (ft > 0) out.ft = Math.round(ft);
  const surf = blocks.flatMap((b) => (Array.isArray(b.surfaces) ? b.surfaces : [])).filter((s, i, a) => typeof s === "string" && s && a.indexOf(s) === i);
  if (surf.length) out.surfaces = surf;
  return out;
}

/* the plans a finished concrete job starts: one per care type on it, dated
   from the day it was done. `extra` adds a type the job didn't have (a
   re-seal offered after a lift — its scope is the lift's area). Nothing for
   a fence job or a job with no care types. Ids are stable per job + type so a
   second completion (a re-run, a sync echo) replaces rather than doubles. */
export function plansFromJob(wo: any, doneYmd: string, opts: { every?: Partial<Record<CareType, number>>; extra?: CareType[]; now?: number } = {}): CarePlan[] {
  if (!wo || !wo.id || typeof doneYmd !== "string" || !YMD.test(doneYmd)) return [];
  if (wo.vertical !== "concrete" && !(wo.cx && Array.isArray(wo.cx.blocks))) return [];
  const every = { ...CARE_EVERY, ...(opts.every || {}) };
  const blocks = careBlocksOf(wo);
  const types: CareType[] = blocks.map((b) => b.jobType as CareType).filter((t, i, a) => a.indexOf(t) === i);
  (opts.extra || []).forEach((t) => { if (isCareType(t) && !types.includes(t)) types.push(t); });
  const allBlocks = wo.cx && Array.isArray(wo.cx.blocks) ? wo.cx.blocks : [];
  return types.map((jobType) => {
    const own = blocks.filter((b) => b.jobType === jobType);
    // an added type borrows the job's whole footprint (a re-seal over what was lifted)
    const scope = scopeOf(own.length ? own : allBlocks.filter((b: any) => b && b.jobType !== "caulk"));
    const months = Math.max(1, Math.round(Number(every[jobType]) || CARE_EVERY[jobType]));
    return {
      id: "cp-" + wo.id + "-" + jobType, vertical: "concrete", jobType, everyMonths: months,
      nextDue: addMonths(doneYmd, months), lastDone: doneYmd,
      sourceQuoteId: wo.quoteId || null, sourceWoId: wo.id, lastQuoteId: null,
      scope, status: "active", createdAt: opts.now || Date.now(), remindedFor: null,
    };
  });
}

/* existing plans + fresh ones, by id (fresh wins); a plan the owner paused stays paused */
export function mergePlans(existing: any, fresh: CarePlan[]): CarePlan[] {
  const cur: CarePlan[] = Array.isArray(existing) ? existing.filter((p) => p && p.id) : [];
  const out = cur.slice();
  (fresh || []).forEach((p) => {
    const i = out.findIndex((x) => x.id === p.id);
    if (i < 0) out.push(p);
    else out[i] = { ...p, status: out[i].status === "paused" ? "paused" : p.status, createdAt: out[i].createdAt || p.createdAt };
  });
  return out;
}

/* the rebooked job is done: roll the plan to its next date */
export function rollPlan(plan: CarePlan, doneYmd: string): CarePlan {
  if (!plan || !YMD.test(String(doneYmd))) return plan;
  return { ...plan, lastDone: doneYmd, nextDue: addMonths(doneYmd, plan.everyMonths), lastQuoteId: null, remindedFor: null };
}
/* the plans whose rebooked quote this job came from */
export function rollPlansFor(plans: any, quoteId: any, doneYmd: string): CarePlan[] {
  if (!Array.isArray(plans) || !quoteId) return Array.isArray(plans) ? plans : [];
  return plans.map((p) => (p && p.lastQuoteId === quoteId ? rollPlan(p, doneYmd) : p));
}

/* is it time? within `withinDays` of nextDue (overdue counts), active only */
export function planDue(plan: any, todayYmd: string, withinDays = 45): { due: boolean; days: number } {
  if (!plan || plan.status !== "active" || !YMD.test(String(plan.nextDue))) return { due: false, days: NaN };
  const days = daysBetween(todayYmd, plan.nextDue);
  return { due: days <= withinDays, days };
}
/* every customer's plans that are due, soonest first — the To-Do's rows */
export function duePlans(customers: any, todayYmd: string, withinDays = 45): { customer: any; plan: CarePlan; days: number }[] {
  const out: { customer: any; plan: CarePlan; days: number }[] = [];
  (Array.isArray(customers) ? customers : []).forEach((c: any) => {
    (c && Array.isArray(c.carePlans) ? c.carePlans : []).forEach((p: any) => {
      const d = planDue(p, todayYmd, withinDays);
      if (d.due && !p.lastQuoteId) out.push({ customer: c, plan: p, days: d.days });   // already rebooked → the quote carries it
    });
  });
  return out.sort((a, b) => a.days - b.days);
}

/* the blocks a fresh estimate starts from: the plan's own scope, no rate
   (priced off today's cards — lib/engine.concrete.ts buildConcreteQuote) */
export function rebookBlocks(plan: any): any[] {
  if (!plan || !isCareType(plan.jobType)) return [];
  const s = plan.scope || {};
  if (plan.jobType === "caulk") return [{ jobType: "caulk", ft: Number(s.ft) || 0, sqft: 0, surfaces: Array.isArray(s.surfaces) ? s.surfaces : [], rate: null }];
  return [{ jobType: plan.jobType, sqft: Number(s.sqft) || 0, ft: 0, surfaces: Array.isArray(s.surfaces) ? s.surfaces : [], rate: null }];
}

export function planLabel(plan: any): string {
  if (!plan) return "";
  const s = plan.scope || {};
  const size = plan.jobType === "caulk" ? (s.ft ? s.ft + " ft of joints" : "") : (s.sqft ? s.sqft.toLocaleString("en-US") + " sq ft" : "");
  return [CARE_LABEL[plan.jobType as CareType] || plan.jobType, size, everyLabel(plan.everyMonths)].filter(Boolean).join(" · ");
}
/* the words of the reminder: "it's been about 2 years since we sealed your driveway" */
export function sinceLabel(plan: any, todayYmd: string): string {
  if (!plan || !YMD.test(String(plan.lastDone))) return "a while";
  const days = daysBetween(plan.lastDone, todayYmd);
  const months = Math.round(days / 30.4);
  if (months < 1) return "a few weeks";
  if (months < 11) return months + " months";
  const years = Math.round(months / 12);
  return years <= 1 ? "a year" : years + " years";
}

/* what the hub may show (mirrors server/src/hubscope.js CUSTOMER_CARE_FIELDS) */
export function hubPlans(plans: any): { jobType: CareType; nextDue: string; everyMonths: number }[] {
  return (Array.isArray(plans) ? plans : []).filter((p: any) => p && p.status === "active" && isCareType(p.jobType) && YMD.test(String(p.nextDue)))
    .map((p: any) => ({ jobType: p.jobType, nextDue: p.nextDue, everyMonths: Number(p.everyMonths) || CARE_EVERY[p.jobType as CareType] }));
}
